/**
 * Shared Receipt detail page — works for owner_builder, architect,
 * and platform_admin. The `apiPrefix` is passed in by the route wrapper.
 * Architect / admin pages show extra "platform fee" / "net to firm" rows.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { api } from "../lib/api";
import {
  ArrowLeft, CheckCircle2, Download, Printer, CreditCard,
} from "lucide-react";
import BackBar from "../components/BackBar";

function money(amount, currency = "USD") {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}

function dateLabel(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function rolePortalLabel(role) {
  if (role === "architect") return "Architect";
  if (role === "platform_admin") return "Admin";
  return "Owner";
}
function rolePortalHome(role) {
  if (role === "architect") return "/architect";
  if (role === "platform_admin") return "/admin";
  return "/owner";
}

export default function ReceiptPage({ apiPrefix, backTo, role }) {
  const { transactionRef } = useParams();
  const { user } = useRole();
  const effectiveRole = role || user?.role;
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    setR(null);
    api
      .get(`${apiPrefix}/payments/${transactionRef}?user_id=${user.id}`)
      .then((res) => setR(res.data))
      .catch((e) => setErr(e));
  }, [transactionRef, user?.id, apiPrefix]);

  if (err) {
    return (
      <div data-testid="receipt-error" style={{ color: "#A23B2E" }}>
        Could not load this receipt.{" "}
        <Link to={backTo} style={{ color: "#1B1F4B" }}>
          Back to payments
        </Link>
      </div>
    );
  }
  if (!r) {
    return (
      <div style={{ color: "#6C6B85", fontStyle: "italic" }}>
        Loading receipt…
      </div>
    );
  }

  // The architect / admin endpoints return the raw payment item with extra
  // `platform_fee` / `net_to_firm` fields; line_items is built only by the
  // owner endpoint. Synthesize line items for the other roles.
  const lineItems = r.line_items
    || (r.kind === "consultation"
        ? [{ label: "Architect consultation · 45 min",
             amount: r.amount, qty: 1 }]
        : [{ label: "Plan-set base fee", amount: 2500, qty: 1 },
           { label: `Per-square-foot (${r.design?.sqft || 0} sqft × $1)`,
             amount: (r.design?.sqft || 0), qty: 1 }]);

  const subtotal = (lineItems || []).reduce((s, x) => s + (x.amount || 0), 0)
    || r.amount || 0;
  const total = r.amount || subtotal;
  const platformFee = r.platform_fee;
  const netToFirm = r.net_to_firm;

  return (
    <section className="animate-fade-in-up" data-testid="receipt-page" style={{ maxWidth: 880 }}>
      <BackBar
        parent={{ to: backTo, label: "Payments" }}
        trail={[
          { label: rolePortalLabel(effectiveRole), to: rolePortalHome(effectiveRole) },
          { label: "Payments", to: backTo },
          { label: r.transaction_ref || transactionRef },
        ]}
      />

      <div data-testid="receipt-card" style={{
        border: "1px solid #D9D8E4", borderRadius: 4, backgroundColor: "#FBFAFD",
      }}>
        <div className="grid grid-cols-[1fr_auto] items-start" style={{
          padding: "32px 36px 24px", borderBottom: "1px solid #D9D8E4", gap: 24,
        }}>
          <div>
            <div className="wn-label" style={{ color: "#8F3C23" }}>
              {r.label} · Receipt
            </div>
            <div className="mt-2" style={{
              fontFamily: '"Fraunces", Georgia, serif', fontWeight: 500,
              fontSize: 32, color: "#1B1F4B", lineHeight: 1.1,
              letterSpacing: "-0.005em",
            }}>
              {money(total, r.currency || "USD")}
              <span style={{
                fontFamily: '"Nunito Sans", sans-serif', fontSize: 14,
                color: "#6C6B85", fontWeight: 500, marginLeft: 4,
                letterSpacing: "0.04em",
              }}>
                {(r.currency || "USD").toUpperCase()}
              </span>
            </div>
            <div className="mt-3 inline-flex items-center gap-2" style={{
              padding: "4px 10px", borderRadius: 999,
              background: "rgba(79,97,73,0.12)", color: "#4F6149",
              fontFamily: '"Nunito Sans", sans-serif', fontWeight: 700,
              fontSize: 11, letterSpacing: "0.10em", textTransform: "uppercase",
            }} data-testid="receipt-status">
              <CheckCircle2 size={12} strokeWidth={2.2} /> Succeeded
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" data-testid="receipt-print-btn"
              onClick={() => window.print()}
              className="wn-btn wn-btn-secondary" style={{ fontSize: 12 }}>
              <Printer size={13} /> Print
            </button>
            <button type="button" data-testid="receipt-download-btn"
              onClick={() => window.print()}
              className="wn-btn wn-btn-secondary" style={{ fontSize: 12 }}>
              <Download size={13} /> PDF
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3" style={{ padding: "24px 36px", gap: 24 }}>
          <Field label={effectiveRole === "owner_builder" ? "Billed to" : "Paid by"}>
            <div style={{ fontWeight: 600, color: "#1B1F4B" }}>
              {(r.buyer?.name || r.owner?.name) || "—"}
            </div>
            {(r.buyer?.organization || r.owner?.organization) && (
              <div style={{ color: "#272636", fontSize: 13, marginTop: 2 }}>
                {r.buyer?.organization || r.owner?.organization}
              </div>
            )}
            {r.buyer?.email && (
              <div style={{ color: "#6C6B85", fontSize: 13, marginTop: 2 }}>
                {r.buyer.email}
              </div>
            )}
          </Field>
          <Field label="Payment date">
            <div className="tabular-nums" style={{
              color: "#1B1F4B", fontWeight: 600,
            }}>
              {dateLabel(r.paid_at)}
            </div>
            <div className="flex items-center gap-2 mt-1" style={{
              color: "#272636", fontSize: 13,
            }}>
              <CreditCard size={13} color="#6C6B85" strokeWidth={1.6} />
              {r.payment_method || "Visa"} ending{" "}
              <span className="tabular-nums">{r.last4 || "4242"}</span>
            </div>
          </Field>
          <Field label="Reference">
            <div className="tabular-nums" style={{
              fontFamily: '"Nunito Sans", sans-serif', fontWeight: 700,
              color: "#1B1F4B", fontSize: 14, letterSpacing: "0.04em",
            }}>
              {r.transaction_ref}
            </div>
          </Field>
        </div>

        <div style={{ padding: "0 36px 24px" }}>
          <div className="wn-label" style={{ color: "#6C6B85" }}>Line items</div>
          <table className="mt-3" style={{
            width: "100%", borderCollapse: "collapse",
            fontFamily: '"Nunito Sans", sans-serif',
          }}>
            <tbody>
              {(lineItems || []).map((li, i) => (
                <tr key={i} style={{ borderTop: "1px solid #ECEBF1" }}
                  data-testid={`receipt-line-${i}`}>
                  <td style={{ padding: "12px 0", fontSize: 14, color: "#272636" }}>
                    {li.label}
                  </td>
                  <td className="tabular-nums" style={{
                    padding: "12px 0", textAlign: "right", width: 120,
                    color: "#1B1F4B", fontWeight: 600,
                  }}>
                    {money(li.amount)}
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: "1px solid #D9D8E4" }}>
                <td className="wn-label" style={{ padding: "14px 0 0", color: "#6C6B85" }}>
                  Subtotal
                </td>
                <td className="tabular-nums" style={{
                  padding: "14px 0 0", textAlign: "right", color: "#272636",
                }}>
                  {money(subtotal)}
                </td>
              </tr>
              {platformFee != null && (
                <tr>
                  <td className="wn-label" style={{ padding: "6px 0 0", color: "#6C6B85" }}>
                    Platform fee (5%)
                  </td>
                  <td className="tabular-nums" style={{
                    padding: "6px 0 0", textAlign: "right", color: "#6C6B85",
                  }}>
                    {effectiveRole === "platform_admin" ? "+" : "−"}{money(platformFee).replace("$", "$")}
                  </td>
                </tr>
              )}
              {platformFee == null && (
                <tr>
                  <td className="wn-label" style={{ padding: "6px 0 0", color: "#6C6B85" }}>
                    Platform fee
                  </td>
                  <td className="tabular-nums" style={{
                    padding: "6px 0 0", textAlign: "right", color: "#6C6B85",
                  }}>
                    Included
                  </td>
                </tr>
              )}
              <tr>
                <td style={{
                  padding: "14px 0 0", color: "#1B1F4B", fontWeight: 700,
                  fontFamily: '"Fraunces", Georgia, serif', fontSize: 18,
                }}>
                  {effectiveRole === "architect"
                    ? "Net to firm"
                    : effectiveRole === "platform_admin"
                    ? "Platform revenue"
                    : "Total charged"}
                </td>
                <td className="tabular-nums" style={{
                  padding: "14px 0 0", textAlign: "right",
                  color: "#1B1F4B", fontWeight: 800, fontSize: 18,
                }}>
                  {effectiveRole === "architect"
                    ? money(netToFirm ?? total)
                    : effectiveRole === "platform_admin"
                    ? money(platformFee ?? 0)
                    : money(total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={{ padding: "20px 36px 28px", borderTop: "1px solid #D9D8E4" }}>
          <div className="wn-label" style={{ color: "#6C6B85" }}>
            Linked records
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: "10px 0 0" }}>
            {r.design?.id && (
              <LinkedRow label="Design" value={r.design.name}
                to={`/marketplace/${r.design.id}`} />
            )}
            {r.parcel?.id && (
              <LinkedRow label="Parcel" value={r.parcel.address || r.parcel.id}
                to={`/parcels/${r.parcel.id}`} />
            )}
            {r.architect?.id && (
              <LinkedRow label="Architect" value={r.architect.firm || r.architect.name}
                to={`/architects/${r.architect.id}`} />
            )}
            {r.owner?.name && effectiveRole !== "owner_builder" && (
              <LinkedRow label="Paid by" value={
                r.owner.organization || r.owner.name
              } to={"#"} disabled />
            )}
          </ul>
        </div>
      </div>

      <p className="mt-6" style={{
        fontFamily: '"Newsreader", Georgia, serif', fontStyle: "italic",
        color: "#6C6B85", fontSize: 13, lineHeight: 1.55, maxWidth: 700,
      }}>
        WHOLE&nbsp;Neighborhoods is a registered platform under the City of
        Indianapolis Department of Metropolitan Development. Refund requests
        must be filed within 30 days of the transaction date. Reach support at{" "}
        <span style={{ color: "#1B1F4B" }}>billing@wholeneighborhoods.org</span>.
      </p>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="wn-label" style={{ color: "#6C6B85" }}>{label}</div>
      <div className="mt-2" style={{ fontSize: 14, lineHeight: 1.45 }}>
        {children}
      </div>
    </div>
  );
}

function LinkedRow({ label, value, to, disabled }) {
  return (
    <li className="grid grid-cols-[120px_1fr] items-baseline" style={{
      padding: "8px 0", borderTop: "1px solid #ECEBF1",
    }}>
      <span className="wn-label" style={{ color: "#6C6B85" }}>{label}</span>
      {disabled ? (
        <span style={{ color: "#1B1F4B", fontWeight: 600, fontSize: 14 }}>
          {value}
        </span>
      ) : (
        <Link to={to} style={{
          color: "#1B1F4B", fontWeight: 600, fontSize: 14,
          textDecoration: "none",
        }}>
          {value} →
        </Link>
      )}
    </li>
  );
}
